import {
  Dispatch,
  KeyboardEvent,
  MutableRefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { sendTagging } from '../../store/ducks/tagging/actions';
import { useDispatch, useSelector } from 'react-redux';
import { ISearchResult } from '@gerdau/hefesto/SearchField/SearchField.types';
import useDebounce from '../../hooks/useDebounce';
import { ApplicationState } from '../../store';
import { updateLongSteelShoppingCartSettings } from '../../store/ducks/long-steel-shopping-cart/actions';
import { addToast } from '../../store/ducks/toast/actions';
import { EToastTypes } from '../../store/ducks/toast/types';
import { useApiInstance } from '../../services/MVP/useApiInstance';
import { useAuth } from '../../services/MVP/useAuth';
import { EndpointsMvp } from '../../services/MVP/enum';
import { useWishlistService } from '../../services/MVP/useWishlistService';
import { usePricingService } from '../../services/MVP/usePricingService';
import { CartItemPayload, SortProductsItem } from '../../services/MVP/models';
import { useProductForCustomer } from '../../services/MVP/useProductForCustomer';
import { useCart } from '../Purchase/LongSteel/ShoppingCartMVP/useCart';
import { useCartService } from '../../services/MVP/useCartService';
import { trackPurchaseAttempt } from '../../services/MVP/purchaseAttemptService';
import { updateCartItemsQuantity } from '../../store/ducks/cart-items-mvp/actions';
import { useLoadingModal } from '../../context/useModaContext';
import { IShipToCustomerItem } from '../../services/EntryOrder';
import {
  loadReceiversForBP,
  loadReceiversFrom
} from '../../store/ducks/entry-order/actions';
import {
  loadCustomerFromId,
  loadCustomersSuccess
} from '../../store/ducks/customers/actions';
import { GetFlag } from '../../utils/featureFlag';
import { uppercaseOnlyFirstLetter } from '../../utils/formatStrings';
import useIsCommerceUser from '../../hooks/useIsCommerceUser';
import { filterInvalidCustomers } from '../../utils/commerceUser';
import {
  setTipoCompraForItemId,
  setLastTipoCompra
} from '../../services/MVP/tipoCompraMap';

export type MaterialCode = 'gerdauCode' | 'internalCode';

const DEFAULT_PAGE_SIZE = 12;

const EMPTY_RECEIVERS: IShipToCustomerItem[] = [];

let loadCartRunning = false;

export interface FavoritesHandlers {
  userLoaded: boolean;
  isMVPUser: boolean;
  isUserAdmin: boolean;
  newSearchEmitterInputAdmin: boolean;
  loading: boolean;
  receiverLoading: boolean;
  customersLoading: boolean;
  bpError: boolean;
  emitterId: string;
  receiverId: string;
  emitterOptions: Array<{
    label: string;
    value: string;
    descriptions: string[];
  }>;
  receiverOptions: Array<{
    label: string;
    value: string;
    descriptions: string[];
  }>;
  isEmitterDisabled: () => boolean;
  isReceiverDisabled: () => boolean;
  handleSelectEmitter: (id: string) => void;
  handleSelectReceiver: (id: string) => void;
  searchCustomerById: (id: string) => void;
  customerName: string | undefined;
  allFavorites: SortProductsItem[];
  displayedProducts: SortProductsItem[];
  total: number;
  currentPage: number;
  materialCode: MaterialCode;
  setMaterialCode: Dispatch<SetStateAction<MaterialCode>>;
  searchInput: string;
  suggestions: ISearchResult[];
  searchRef: MutableRefObject<HTMLInputElement | null>;
  handleSelectSuggestion: (result: ISearchResult) => Promise<void>;
  handleSearch: () => void;
  handleClearSearch: () => void;
  handleKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  handleSearchInputChange: (value: string) => void;
  pageSize: number;
  handlePageChange: (page: number, newPageSize: number) => void;
  handleRemoveFavorite: (sku: string) => Promise<void>;
  showSearchAndFilters: boolean;
  paymentCondition: string | undefined;
  hasEmitterAndReceiver: boolean;
  submittedSearch: string;
  shouldShowModal: boolean;
  handleOrderModal: (option?: 'continuous' | 'new') => Promise<void>;
  showBpUnavailableModal: boolean;
  onCloseBpUnavailableModal: () => void;
  hasOnlyCgCustomers: boolean;
}

export const useFavorites = (): FavoritesHandlers => {
  const dispatch = useDispatch();
  const isMVPUser = useIsCommerceUser();
  const newEmitterReceiverSelectFlag = GetFlag('new_emitter_receiver_select');
  const newSearchEmitterInputAdmin = GetFlag('new_search_emitter_input_admin');
  const commerceApi = useApiInstance();
  const { getHeaders } = useAuth();
  const { removeFromWishlist } = useWishlistService();
  const { getPrice } = usePricingService();
  const { fetchProductDetailByCatalog } = useProductForCustomer();
  const { addItemToCart, deleteCartId } = useCart();
  const { createNewCart, deactivateCart, getCartId } = useCartService();
  const newCartEndpoint = GetFlag('new_cart_endpoints');
  const migrationFlag = GetFlag('purchase-migration-brdi-brin');
  const filterEmittersFlag = GetFlag('filter_emitters');
  const { isLoadingModalOpen, setLoadingModalOpen } = useLoadingModal();

  const cartQuantity = useSelector(
    (state: ApplicationState) => state.cartItemsMvp.quantity
  );
  const shoppingCartState = useSelector(
    (state: ApplicationState) => state.longSteelShoppingCart
  );
  const isUserAdmin = useSelector(
    (state: ApplicationState) => state.user.isManager
  );
  const userLoaded = useSelector(
    (state: ApplicationState) => state.user.userLoaded
  );
  const userState = useSelector((state: ApplicationState) => state.user);
  const purchaseCustomers = useSelector((state: ApplicationState) =>
    filterInvalidCustomers(
      isUserAdmin
        ? state.customers.customers
        : state.customers.customers.filter((customer) =>
            userState.roles.ordersInputCustomers.includes(customer.customerId)
          ),
      filterEmittersFlag
    )
  );
  const receiverOptionsRedux = useSelector(
    (state: ApplicationState) =>
      state.entryOrderReceivers?.receivers ?? EMPTY_RECEIVERS
  );
  const receiverLoading = useSelector(
    (state: ApplicationState) => state.entryOrderReceivers?.loading ?? false
  );
  const receiversCustomerId = useSelector(
    (state: ApplicationState) => state.entryOrderReceivers?.customerId ?? ''
  );
  const {
    customers,
    loading: loadingCustomers,
    error: customersError,
    errorBp,
    loadingProductDescription,
    loadingPurchaseReceivers,
    loadingBlocks,
    loadingCustomerById
  } = useSelector((state: ApplicationState) => state.customers);

  const { emitterId, receiverId } = shoppingCartState;

  const selectedCustomer = customers.find((c) => c.customerId === emitterId);

  const isBlockedCustomer =
    selectedCustomer?.blocks &&
    selectedCustomer?.blocks?.filter((blc) => blc.type === 'order')?.length > 0;

  const newSearchItemsEndpointFlag = GetFlag('new_search_items_endpoint');

  const descriptionResponse = Boolean(
    newSearchItemsEndpointFlag || selectedCustomer?.productsDescriptionResponse
  );

  const [allFavorites, setAllFavorites] = useState<SortProductsItem[]>([]);
  const [displayedProducts, setDisplayedProducts] = useState<
    SortProductsItem[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [materialCode, setMaterialCode] = useState<MaterialCode>('gerdauCode');
  const [searchInput, setSearchInput] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [suggestions, setSuggestions] = useState<ISearchResult[]>([]);
  const [orderModalStatus, setOrderModalStatus] = useState(false);
  const [shouldShowModal, setShouldShowModal] = useState(false);

  const [showBpUnavailableModal, setShowBpUnavailableModal] = useState(false);

  const submittedSearchRef = useRef(submittedSearch);
  submittedSearchRef.current = submittedSearch;
  const searchRef = useRef<HTMLInputElement | null>(null);
  const isSelectingSuggestionRef = useRef(false);
  const cgModalCheckRef = useRef<(() => void) | null>(null);
  const cgModalShownForRef = useRef<string>('');
  const isMaterialCodeFirstRender = useRef(true);
  const isRemovingFavoriteRef = useRef(false);
  const loadedCartPairRef = useRef<string>('');

  const customerLoading =
    loadingCustomers ||
    !!loadingProductDescription ||
    !!loadingPurchaseReceivers ||
    !!loadingBlocks ||
    !!loadingCustomerById;

  const cartId = getCartId(emitterId);

  useEffect(() => {
    if (orderModalStatus && !isLoadingModalOpen) {
      const timer = setTimeout(() => setShouldShowModal(true), 1000);
      return () => clearTimeout(timer);
    } else {
      setShouldShowModal(false);
    }
  }, [orderModalStatus, isLoadingModalOpen]);

  useEffect(() => {
    if (!(isUserAdmin && newSearchEmitterInputAdmin)) return;
    dispatch(
      updateLongSteelShoppingCartSettings({
        ...shoppingCartState,
        emitterId: '',
        receiverId: ''
      })
    );
  }, []);

  const resetSelections = () => {
    dispatch(
      updateLongSteelShoppingCartSettings({
        ...shoppingCartState,
        emitterId: '',
        receiverId: ''
      })
    );
  };

  useEffect(() => {
    const pairKey = `${emitterId}:${receiverId}`;
    if (customerLoading || !emitterId || !receiverId) return;
    if (migrationFlag && selectedCustomer?.customerType === 'CG') return;
    if (loadedCartPairRef.current === pairKey) return;

    loadedCartPairRef.current = pairKey;

    const loadCart = async () => {
      try {
        const data = await createNewCart(
          emitterId,
          receiverId || emitterId,
          undefined,
          true,
          false,
          true
        );
        if (data?.alreadyCart && !isLoadingModalOpen) {
          setOrderModalStatus(true);
        }
      } catch {
        dispatch(
          addToast({
            type: EToastTypes.DANGER,
            message: 'Não foi possível alterar o recebedor do pedido',
            durationInMS: 5000
          })
        );
        loadedCartPairRef.current = '';
        resetSelections();
      }
    };

    if (!loadCartRunning) {
      loadCartRunning = true;
      loadCart()
        .then(() => {
          loadCartRunning = false;
        })
        .catch(() => {
          loadCartRunning = false;
        });
    }
  }, [emitterId, receiverId, customerLoading]);

  const isEmitterDisabled = (): boolean =>
    customerLoading ||
    customersError ||
    (!isUserAdmin && purchaseCustomers.length === 1 && !!emitterId) ||
    (!!cartId && cartQuantity > 0);

  const isReceiverDisabled = (): boolean =>
    !emitterId ||
    customerLoading ||
    receiverLoading ||
    customersError ||
    (!isUserAdmin && purchaseCustomers.length === 1) ||
    (!!cartId && cartQuantity > 0);

  const fetchGenRef = useRef(0);
  const fetchedPairRef = useRef<string>('');

  const hasEmitterAndReceiver = Boolean(emitterId && receiverId);

  const fetchFavorites = useCallback(
    async (page = 1, searchTerm = '', size = pageSize) => {
      if (!emitterId || !receiverId) return;

      const gen = ++fetchGenRef.current;
      setLoading(true);
      try {
        const params: Record<string, string> = {
          'searchCriteria[currentPage]': String(page),
          'searchCriteria[pageSize]': String(size),
          suggest: 'false'
        };

        if (searchTerm.trim()) {
          const term = searchTerm.trim();
          if (materialCode === 'internalCode') {
            params['searchCriteria[filter_groups][0][filters][0][field]'] =
              'internal_code';
            params[
              'searchCriteria[filter_groups][0][filters][0][value]'
            ] = `%${term}%`;
            params[
              'searchCriteria[filter_groups][0][filters][0][condition_type]'
            ] = 'like';
          } else {
            params['searchCriteria[filter_groups][0][filters][0][field]'] =
              'sku';
            params['searchCriteria[filter_groups][0][filters][0][value]'] =
              term;
            params[
              'searchCriteria[filter_groups][0][filters][0][condition_type]'
            ] = 'in';
            params['searchCriteria[filter_groups][0][filters][1][field]'] =
              'name';
            params[
              'searchCriteria[filter_groups][0][filters][1][value]'
            ] = `%${term}%`;
            params[
              'searchCriteria[filter_groups][0][filters][1][condition_type]'
            ] = 'like';
          }
        }

        const response = await commerceApi.get(
          EndpointsMvp.WISHLIST_LIST(emitterId, receiverId),
          { params, headers: getHeaders({ withCommerceToken: true }) }
        );
        if (gen !== fetchGenRef.current) return;
        const items: SortProductsItem[] =
          response.data?.items ?? response.data ?? [];
        const totalCount: number = response.data?.total_count ?? items.length;
        const favorites = items.filter(
          (p) => p.extension_attributes?.added_to_wishlist
        );

        setAllFavorites(favorites);
        setTotal(totalCount);
        setDisplayedProducts(favorites);
      } catch {
        if (gen !== fetchGenRef.current) return;
        dispatch(
          addToast({
            type: EToastTypes.DANGER,
            message: 'Não foi possível carregar os favoritos. Tente novamente.',
            durationInMS: 5000
          })
        );
      } finally {
        if (gen === fetchGenRef.current) setLoading(false);
      }
    },
    [emitterId, receiverId, dispatch, materialCode, pageSize]
  );

  const fetchSuggestions = async (term: string) => {
    if (!emitterId || !receiverId || term.trim().length <= 2) {
      setSuggestions([]);
      return;
    }
    try {
      const params: Record<string, string> = {
        'searchCriteria[currentPage]': '1',
        'searchCriteria[pageSize]': '10',
        suggest: 'true',
        ...(materialCode === 'internalCode'
          ? {
              'searchCriteria[filter_groups][0][filters][0][field]':
                'internal_code',
              'searchCriteria[filter_groups][0][filters][0][value]': `%${term.trim()}%`,
              'searchCriteria[filter_groups][0][filters][0][condition_type]':
                'like'
            }
          : {
              'searchCriteria[filter_groups][0][filters][0][field]': 'sku',
              'searchCriteria[filter_groups][0][filters][0][value]':
                term.trim(),
              'searchCriteria[filter_groups][0][filters][0][condition_type]':
                'in',
              'searchCriteria[filter_groups][0][filters][1][field]': 'name',
              'searchCriteria[filter_groups][0][filters][1][value]': `%${term.trim()}%`,
              'searchCriteria[filter_groups][0][filters][1][condition_type]':
                'like'
            })
      };
      const response = await commerceApi.get(
        EndpointsMvp.WISHLIST_LIST(emitterId, receiverId),
        { params, headers: getHeaders({ withCommerceToken: true }) }
      );
      const items: SortProductsItem[] =
        response.data?.items ?? response.data ?? [];
      const favoriteItems = items.filter(
        (p) => p.extension_attributes?.added_to_wishlist
      );
      setSuggestions(
        favoriteItems.map((p, i) => ({
          id: `${p.sku}-${i}`,
          label: `${p.sku} - ${p.name}`
        }))
      );
      dispatch(
        sendTagging({
          event: 'view_search_results',
          screenName: 'pagina_favoritos',
          etapa: 'pesquisa_favoritos',
          search_term: term
        })
      );
      if (searchRef.current) {
        searchRef.current.blur();
        searchRef.current.focus();
      }
    } catch {
      setSuggestions([]);
    }
  };

  const debouncedFetchSuggestions = useDebounce(fetchSuggestions, 800);

  const handleSelectSuggestion = async (result: ISearchResult) => {
    isSelectingSuggestionRef.current = true;
    const sku = result.id.split('-')[0].trim();
    try {
      setLoading(true);
      const product = await fetchProductDetailByCatalog(sku);

      if (
        !product ||
        !product.price ||
        Number(product.price.creditPrice) <= 0
      ) {
        dispatch(
          addToast({
            type: EToastTypes.DANGER,
            message: `Produto indisponível. (SKU ${product?.sku ?? sku})`,
            durationInMS: 5000,
            labelActionOne: 'Fechar'
          })
        );
        return;
      }

      const existingCart = getCartId(emitterId);
      let cart: string | undefined;
      let currentQuantity: number;

      if (!existingCart || existingCart.length === 0) {
        const newCart = await createNewCart(emitterId, receiverId, true);
        cart = newCart?.cartId;
        currentQuantity = 1;
      } else {
        cart = existingCart;
        currentQuantity = cartQuantity + 1;
      }

      if (!cart || cart.length === 0) {
        throw new Error();
      }

      if (currentQuantity > 30) {
        dispatch(
          addToast({
            type: EToastTypes.DANGER,
            message: 'Seu carrinho atingiu o limite de 30 produtos.',
            durationInMS: 5000,
            labelActionOne: 'Fechar'
          })
        );
        return;
      }

      const formattedProduct = {
        sku: product.sku,
        qty: 1,
        quote_id: cart,
        extension_attributes: {
          measure_unity_gerdau: product.price.um,
          destination_gerdau: product.price.destination,
          payment_type_gerdau: product.price.paymentTerms,
          has_agreed_price_gerdau: product.price.isAgreementPrice.length !== 0,
          ...(product.price.salesAgreementData.expirationDate && {
            price_agreement_date_gerdau:
              product.price.salesAgreementData.expirationDate
          }),
          sap_fields: { o_header_sap: [], o_item_sap: [], o_price_sap: [] }
        }
      } as CartItemPayload;

      const addItem = await addItemToCart(formattedProduct, currentQuantity, {
        withOptions: false,
        withPrice: false
      });

      if (addItem.item_id) {
        dispatch(updateCartItemsQuantity(currentQuantity));
        dispatch(
          sendTagging({
            event: 'add_to_cart',
            ecommerce: {
              currency: 'BRL',
              value: `${product.price.creditPrice}`,
              screenName: 'pagina_favoritos',
              tipo_compra: 'favoritos',
              items: [
                {
                  screenName: 'pagina_favoritos',
                  price: `${product.price.creditPrice}`,
                  unidade_de_medida: `${product.price.um}`,
                  item_id: `${product.sku}`,
                  item_name: `${product.name}`,
                  tipo_compra: 'favoritos',
                  item_category: `${product.type_id}`,
                  cod_gerdau: `${product.sku}`,
                  preco_acordado: product.price.isAgreementPrice.length > 0,
                  quantity: '1',
                  favoritado: true
                }
              ]
            }
          })
        );
        setTipoCompraForItemId(addItem.item_id, 'favoritos');
        setLastTipoCompra('favoritos');
        dispatch(
          addToast({
            message: `Produto adicionado ao carrinho. (SKU ${product.sku})`,
            type: EToastTypes.SUCCESS,
            durationInMS: 5000,
            spaceToTop: 77
          })
        );
        return;
      }

      throw new Error();
    } catch {
      dispatch(
        addToast({
          type: EToastTypes.DANGER,
          message:
            'Não foi possível adicionar o produto ao carrinho. Tente novamente.',
          durationInMS: 5000
        })
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const pairKey = `${emitterId}:${receiverId}`;

    if (
      !customerLoading &&
      !receiverLoading &&
      receiversCustomerId === emitterId &&
      emitterId &&
      receiverId &&
      selectedCustomer &&
      !isBlockedCustomer &&
      !(migrationFlag && selectedCustomer?.customerType === 'CG') &&
      fetchedPairRef.current !== pairKey
    ) {
      fetchedPairRef.current = pairKey;
      setCurrentPage(1);
      fetchFavorites(1);
    } else if (!emitterId || !receiverId) {
      fetchGenRef.current++;
      setLoading(false);
      setAllFavorites([]);
      setDisplayedProducts([]);
      setTotal(0);
    }
  }, [
    emitterId,
    receiverId,
    customerLoading,
    receiverLoading,
    receiversCustomerId
  ]);

  const handleSelectEmitter = (customerId: string) => {
    if (migrationFlag) {
      const customer = purchaseCustomers.find(
        (c) => c.customerId === customerId
      );
      if (customer?.customerType === 'CG') {
        trackPurchaseAttempt(userState, customerId).catch(() => null);
        setShowBpUnavailableModal(true);
        return;
      }
    }
    dispatch(
      updateLongSteelShoppingCartSettings({
        ...shoppingCartState,
        emitterId: customerId,
        receiverId: ''
      })
    );
    const customer = purchaseCustomers.find((c) => c.customerId === customerId);
    if (customer) {
      dispatch(
        newEmitterReceiverSelectFlag
          ? loadReceiversForBP(customerId)
          : loadReceiversFrom(customer)
      );
    }
  };

  const handleSelectReceiver = (newReceiverId: string) => {
    dispatch(
      updateLongSteelShoppingCartSettings({
        ...shoppingCartState,
        receiverId: newReceiverId
      })
    );
  };

  const searchCustomerById = (id: string) => {
    loadCartRunning = false;
    loadedCartPairRef.current = '';
    fetchedPairRef.current = '';
    cgModalShownForRef.current = '';
    dispatch(loadCustomersSuccess([]));
    dispatch(loadCustomerFromId(id));
    dispatch(
      updateLongSteelShoppingCartSettings({
        ...shoppingCartState,
        emitterId: id,
        receiverId: ''
      })
    );
    dispatch(loadReceiversForBP(id));
  };

  const getCustomer = (customerId: string) =>
    purchaseCustomers.find((c) => c.customerId === customerId);

  const customerName = getCustomer(emitterId)?.name
    ? uppercaseOnlyFirstLetter(getCustomer(emitterId)!.name)
    : undefined;

  const hasOnlyCgCustomers =
    migrationFlag &&
    !isUserAdmin &&
    purchaseCustomers.length > 0 &&
    purchaseCustomers.every((c) => c.customerType === 'CG');

  const onCloseBpUnavailableModal = () => {
    setShowBpUnavailableModal(false);
    dispatch(loadCustomersSuccess([]));
    resetSelections();
  };

  cgModalCheckRef.current = () => {
    if (
      isUserAdmin &&
      !loadingCustomerById &&
      !errorBp &&
      !loadingBlocks &&
      !receiverLoading &&
      emitterId &&
      migrationFlag &&
      cgModalShownForRef.current !== emitterId
    ) {
      const customer = getCustomer(emitterId);
      if (customer?.customerType === 'CG') {
        cgModalShownForRef.current = emitterId;
        trackPurchaseAttempt(userState, emitterId).catch(() => null);
        dispatch(loadCustomersSuccess([]));
        dispatch(
          updateLongSteelShoppingCartSettings({
            ...shoppingCartState,
            emitterId: '',
            receiverId: ''
          })
        );
        setShowBpUnavailableModal(true);
      }
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => cgModalCheckRef.current?.(), 0);
    return () => clearTimeout(timer);
  }, [
    isUserAdmin,
    loadingCustomerById,
    errorBp,
    loadingBlocks,
    receiverLoading,
    emitterId,
    purchaseCustomers
  ]);

  useEffect(() => {
    if (
      !isUserAdmin ||
      emitterId ||
      purchaseCustomers.length !== 2 ||
      loadingCustomerById ||
      errorBp
    )
      return;
    const firstCustomer = purchaseCustomers[0];
    if (migrationFlag && firstCustomer.customerType === 'CG') {
      if (cgModalShownForRef.current !== firstCustomer.customerId) {
        cgModalShownForRef.current = firstCustomer.customerId;
        trackPurchaseAttempt(userState, firstCustomer.customerId).catch(
          () => null
        );
        setShowBpUnavailableModal(true);
      }
      return;
    }
    handleSelectEmitter(firstCustomer.customerId);
  }, [isUserAdmin, emitterId, purchaseCustomers, loadingCustomerById, errorBp]);

  useEffect(() => {
    if (receiverLoading) return;
    if (receiversCustomerId !== emitterId) return;
    if (migrationFlag && selectedCustomer?.customerType === 'CG') return;
    if (receiverOptionsRedux.length === 1 && !receiverId) {
      setLoading(true);
      dispatch(
        updateLongSteelShoppingCartSettings({
          ...shoppingCartState,
          receiverId: receiverOptionsRedux[0].customerId
        })
      );
    }
  }, [receiverOptionsRedux, receiverLoading, receiversCustomerId, emitterId]);

  const handleSearch = () => {
    setSubmittedSearch(searchInput);
    setCurrentPage(1);
    fetchFavorites(1, searchInput);
    dispatch(
      sendTagging({
        event: 'view_search_results',
        screenName: 'pagina_favoritos',
        etapa: 'pesquisa_favoritos',
        search_term: searchInput
      })
    );
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSubmittedSearch('');
    setCurrentPage(1);
    fetchFavorites(1, '');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handlePageChange = (page: number, newPageSize: number) => {
    if (loading) return;
    if (page === currentPage && newPageSize === pageSize) return;
    if (newPageSize !== pageSize) setPageSize(newPageSize);
    setCurrentPage(page);
    fetchFavorites(page, submittedSearchRef.current, newPageSize);
  };

  const handleRemoveFavorite = async (sku: string) => {
    const product = allFavorites.find((p) => p.sku === sku);
    const prdPrice = product ? getPrice(product) : undefined;

    if (product) {
      dispatch(
        sendTagging({
          event: 'remover_favorito',
          screenName: 'pagina_favoritos',
          ecommerce: {
            items: [
              {
                item_id: String(product.sku),
                item_name: product.name,
                item_category: product.type_id,
                ...(prdPrice?.creditPrice && Number(prdPrice.creditPrice) > 0
                  ? { price: String(prdPrice.creditPrice) }
                  : {}),
                quantity: '1',
                unidade_de_medida: prdPrice?.um ?? '',
                cod_gerdau: String(product.sku),
                preco_acordado: (prdPrice?.isAgreementPrice?.length ?? 0) > 0,
                screenName: 'pagina_favoritos',
                tipo_compra: 'favoritos',
                favoritado: false
              }
            ]
          }
        })
      );
    }

    const success = await removeFromWishlist(sku);
    if (success) {
      const updated = allFavorites.filter((p) => p.sku !== sku);
      isRemovingFavoriteRef.current = true;
      setAllFavorites(updated);
      setDisplayedProducts(updated);
      setTotal((prev) => prev - 1);
      dispatch(
        addToast({
          type: EToastTypes.DANGER,
          message: 'Produto removido dos Favoritos',
          durationInMS: 5000
        })
      );
    } else {
      dispatch(
        addToast({
          type: EToastTypes.DANGER,
          message:
            'Não foi possível remover o produto dos favoritos. Tente novamente.',
          durationInMS: 5000
        })
      );
    }
  };

  const handleSearchInputChange = (value: string) => {
    isSelectingSuggestionRef.current = false;
    if (!value) {
      setSearchInput('');
      setSuggestions([]);
      return;
    }
    setSearchInput(value);
    debouncedFetchSuggestions(value);
  };

  const getPaymentCondition = (): string | undefined => {
    for (const prd of allFavorites) {
      const price = getPrice(prd);
      if (price?.paymentTermsDescription) {
        return price.paymentTermsDescription;
      }
    }
    return undefined;
  };

  const emitterOptions = purchaseCustomers.map((customer) => ({
    label: customer.name,
    value: customer.customerId,
    descriptions: [
      `${uppercaseOnlyFirstLetter(
        customer.locality.address
      )} | ${uppercaseOnlyFirstLetter(customer.locality.city)} - ${
        customer.locality.state
      } | CEP ${customer.locality.zipCode}`,
      `N° do cliente ${customer.customerId}`,
      `CNPJ ${customer.taxNumber}`
    ]
  }));

  const receiverOptions = receiverOptionsRedux.map((receiver) => ({
    label: receiver?.name,
    value: receiver?.customerId,
    descriptions: [
      `${uppercaseOnlyFirstLetter(
        receiver.address
      )} | ${uppercaseOnlyFirstLetter(receiver?.city)} - ${
        receiver.state
      } | CEP ${receiver?.postalCode}`,
      `N° do cliente ${receiver?.customerId}`,
      `CNPJ ${receiver?.taxNumber}`
    ]
  }));

  const handleOrderModal = async (option?: 'continuous' | 'new') => {
    if (option === 'new') {
      if (newCartEndpoint) {
        setLoadingModalOpen(true);
        await deactivateCart();
        setLoadingModalOpen(false);
      }
      dispatch(updateCartItemsQuantity(0));
      deleteCartId();
      return setOrderModalStatus(false);
    }
    setOrderModalStatus(false);
  };

  const showSearchAndFilters =
    hasEmitterAndReceiver &&
    !loading &&
    (allFavorites.length > 0 || submittedSearch !== '');

  const paymentCondition = getPaymentCondition();

  useEffect(() => {
    if (displayedProducts.length === 0) return;
    if (isRemovingFavoriteRef.current) {
      isRemovingFavoriteRef.current = false;
      return;
    }
    dispatch(
      sendTagging({
        event: 'view_item_list',
        screenName: 'pagina_favoritos',
        ecommerce: {
          screenName: 'pagina_favoritos',
          paginacao: String(currentPage),
          items: displayedProducts.map((prd) => {
            const prdPrice = getPrice(prd);
            return {
              screenName: 'pagina_favoritos',
              item_id: String(prd.sku),
              item_name: prd.name,
              item_category: prd.type_id,
              ...(prdPrice?.creditPrice && Number(prdPrice.creditPrice) > 0
                ? { price: String(prdPrice.creditPrice) }
                : {}),
              quantity: '1',
              unidade_de_medida: prdPrice?.um ?? '',
              cod_gerdau: String(prd.sku),
              preco_acordado: (prdPrice?.isAgreementPrice?.length ?? 0) > 0,
              tipo_compra: 'favoritos',
              favoritado: true
            };
          })
        }
      })
    );
  }, [displayedProducts]);

  useEffect(() => {
    if (isMaterialCodeFirstRender.current) {
      isMaterialCodeFirstRender.current = false;
      return;
    }
    dispatch(
      sendTagging({
        event: 'filtros_favoritos',
        screenName: 'pagina_favoritos',
        etapa: 'lista_de_favoritos',
        cod_produto: materialCode
      })
    );
    if (submittedSearchRef.current) {
      setCurrentPage(1);
      fetchFavorites(1, submittedSearchRef.current);
    }
  }, [materialCode]);

  return {
    userLoaded,
    isMVPUser,
    isUserAdmin,
    newSearchEmitterInputAdmin,
    loading,
    receiverLoading,
    customersLoading: loadingCustomers,
    bpError: !loadingCustomers && (errorBp ?? false),
    emitterId,
    receiverId,
    emitterOptions,
    receiverOptions,
    isEmitterDisabled,
    isReceiverDisabled,
    handleSelectEmitter,
    handleSelectReceiver,
    searchCustomerById,
    customerName,
    allFavorites,
    displayedProducts,
    total,
    currentPage,
    pageSize,
    materialCode,
    setMaterialCode,
    searchInput,
    suggestions,
    searchRef,
    handleSelectSuggestion,
    handleSearch,
    handleClearSearch,
    handleKeyDown,
    handleSearchInputChange,
    handlePageChange,
    handleRemoveFavorite,
    showSearchAndFilters,
    paymentCondition,
    hasEmitterAndReceiver,
    submittedSearch,
    shouldShowModal,
    handleOrderModal,
    showBpUnavailableModal,
    onCloseBpUnavailableModal,
    hasOnlyCgCustomers
  };
};
